// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useEffect, useRef } from 'react';

// Define the type for the message handler
type MessageHandler = (data: any) => void;

export const useBroadcastChannel = (channelName: string, onMessage?: MessageHandler) => {
    // useRef to store the BroadcastChannel instance
    const channelRef = useRef<BroadcastChannel | null>(null);

    useEffect(() => {
        // Create a BroadcastChannel instance if it doesn't exist
        if (!channelRef.current) {
            channelRef.current = new BroadcastChannel(channelName);
        }

        // Listen for messages
        const channel = channelRef.current;
        channel.onmessage = (event: MessageEvent) => {
            if (onMessage) {
                onMessage(event.data);
            }
        };

        // Cleanup when the component unmounts
        return () => {
            channel.close();
            channelRef.current = null;
        };
    }, [channelName, onMessage]);

    // Function to send a message
    const sendMessage = (message: any) => {
        channelRef.current?.postMessage(message);
    };

    return sendMessage;
};

export const sendMessage = (channelName: string, message?: string) => {
    const channel = new BroadcastChannel(channelName);
    channel.postMessage(message);
    channel.close();
};